import React from 'react';
import { Folder } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '../lib/utils';
import { useProjectTitle } from '../hooks/useProjectTitle';
import { useProjectPageTitle } from '../hooks/usePageTitle';

function pageTitleText(pageTitle: React.ReactNode): string | undefined {
  if (typeof pageTitle === 'string' || typeof pageTitle === 'number') {
    return String(pageTitle);
  }
  return undefined;
}

export function ProjectBreadcrumb({
  pageTitle,
  pageIcon: PageIcon,
}: {
  pageTitle: React.ReactNode;
  pageIcon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
}): React.ReactElement {
  const isMobile = useIsMobile();
  const projectTitle = useProjectTitle();
  useProjectPageTitle(projectTitle, pageTitleText(pageTitle));

  if (!projectTitle) {
    return (
      <div className="flex min-w-0 items-center gap-2">
        {PageIcon && <PageIcon className="shrink-0" style={{ width: 16, height: 16 }} />}
        <span className="min-w-0 truncate leading-normal" style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 600 }}>
          {pageTitle}
        </span>
      </div>
    );
  }

  return (
    <div className={cn('flex min-w-0', isMobile ? 'flex-col items-start gap-0' : 'items-center gap-2')}>
      <div className={cn('flex min-w-0 items-center gap-2', isMobile ? 'max-w-full' : 'shrink-0')}>
        <Folder className="!h-4 !w-4 shrink-0" />
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="min-w-0 truncate leading-normal"
              style={{
                maxWidth: isMobile ? '100%' : 250,
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: 600,
              }}
            >
              {projectTitle}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="start" className="max-w-[min(24rem,calc(100vw-2rem))] break-words">
            {projectTitle}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className={cn('flex min-w-0 items-center gap-2', isMobile && 'w-full')}>
        {!isMobile && <span className="shrink-0 text-muted-foreground">/</span>}
        {PageIcon && <PageIcon className="shrink-0" style={{ width: 16, height: 16 }} />}
        <span className="min-w-0 truncate leading-normal" style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 600 }}>
          {pageTitle}
        </span>
      </div>
    </div>
  );
}
